import {
  Card,
  DiffViewerContentCols,
  DiffViewerTitleCols,
  Row,
} from '@/components';
import { useMinistries } from '@/features/master-table/hooks';
import { diffRenderValueFormatter } from '@/features/project/_utils/diff-render-value-formatter';
import { formatLegalBasis } from '@/features/project/_utils/legal-bases';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import {
  ITEM_SPLITTER,
  NEST_ITEM_CONNECTOR,
  findUpperRecursive,
} from '@/utils';
import {
  ProjectCategoryLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  ProjectRelationTypeLabelModifier,
  type SegmentSheetModel,
} from '../../../_models/project';

type Project = z.infer<typeof SegmentSheetModel>;

type DiffViewConfigItem = DiffViewConfigType<Project>;

type Props = {
  sourceProject: Project; // left side
  targetProject: Project; // right side
};

export const SsProjectDiffView: FC<Props> = ({
  sourceProject,
  targetProject,
}) => {
  const { t } = useTranslation();
  const organizations = useOrganizations();
  const users = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: [
        ...new Set([
          ...(sourceProject.peopleInChargeIds ?? []),
          ...sourceProject.businessManagerIds,
          ...(targetProject.peopleInChargeIds ?? []),
          ...targetProject.businessManagerIds,
        ]),
      ],
      showDeleted: true,
    },
  });

  const ministries = useMinistries();

  const diffViewConfig: DiffViewConfigItem[] = [
    {
      prop: 'sheetType',
      label: t('sheet-type'),
      converter: (value: Project['sheetType']) => SheetTypeLabelModifier[value],
    },
    {
      prop: 'ministryId',
      label: t('ministry'),
      converter: (value: Project['ministryId']) =>
        diffRenderValueFormatter.ministryId(value, ministries.data),
    },
    {
      prop: 'organizationId',
      label: t('organization-in-charge'),
      converter: (value: Project['organizationId']) =>
        findUpperRecursive(value, organizations.data ?? [])
          .reverse()
          .map((d) => d.name)
          .join(NEST_ITEM_CONNECTOR),
    },
    {
      prop: 'peopleInChargeIds',
      label: t('people-in-charge'),
      converter: (value: Project['peopleInChargeIds']) =>
        users.data?.results
          .flatMap((user) => (value?.includes(user.id) ? [user.name] : []))
          .join('\n') ?? '',
    },
    {
      prop: 'businessManagerIds',
      label: t('business-manager'),
      converter: (value: Project['businessManagerIds']) =>
        users.data?.results
          .flatMap((user) => (value.includes(user.id) ? [user.name] : []))
          .join('\n') ?? '',
    },
    {
      prop: 'otherResponsibleOrganizationIds',
      label: t('other-organization-in-charge'),
      converter: (values: Project['otherResponsibleOrganizationIds']) =>
        values
          .map((value) =>
            findUpperRecursive(value, organizations.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR),
          )
          .join('\n'),
    },
    {
      prop: 'relatedOrganizationIds',
      label: t('related-organizations'),
      converter: (values: Project['relatedOrganizationIds']) =>
        values
          .map((value) =>
            findUpperRecursive(value, organizations.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR),
          )
          .join('\n'),
    },
    { prop: 'projectNumber', label: t('project-number') },
    { prop: 'name', label: t('segment-name') },
    {
      prop: 'independentAdministrativeAgencyId',
      label: t('independent-administrative-agency'),
      converter: (value) => {
        return findUpperRecursive(value, organizations.data ?? [])
          .reverse()
          .map((d) => d.name)
          .join(NEST_ITEM_CONNECTOR);
      },
    },
    { prop: 'fiscalYear', label: t('fiscal-year') },
    {
      prop: 'category',
      label: t('project-category'),
      converter: (value: Project['category']) =>
        value ? ProjectCategoryLabelModifier[value] : '',
    },
    {
      prop: 'startYear',
      label: t('start-fiscal-year-of-project'),
      converter: (_, project) =>
        project?.startYear?.toString() ??
        (project?.isUnknownStart ? t('unknown-start') : t('blank')),
    },
    {
      prop: 'endYear',
      label: t('end-fiscal-year-of-project'),
      converter: (_, project) =>
        project?.endYear?.toString() ??
        (project?.isNoScheduledEnd ? t('no-scheduled-end') : t('blank')),
    },
    {
      prop: 'relatedProjects',
      label: t('related-projects'),
      converter: (value: Project['relatedProjects']) =>
        value
          .map(
            (relatedProject) =>
              `${ProjectRelationTypeLabelModifier[relatedProject.relationType]}: ${relatedProject.name}`,
          )
          .join('\n'),
    },
    {
      prop: 'mainCosts',
      label: t('main-cost'),
      converter: (value: Project['mainCosts']) =>
        value.map((cost) => cost.name).join(ITEM_SPLITTER),
    },
    {
      prop: 'segmentUnitConcept',
      label: t('segment-unit-concept'),
      isLongField: true,
    },
    {
      prop: 'purpose',
      label: t('project-purpose'),
      isLongField: true,
    },
    {
      prop: 'task',
      label: t('project-task'),
      isLongField: true,
    },
    {
      prop: 'overview',
      label: t('project-overview'),
      isLongField: true,
    },
    {
      prop: 'overviewUrl',
      label: t('overview-url'),
    },
    {
      prop: 'legalBases',
      label: t('legal-bases'),
      converter: (value: Project['legalBases']) =>
        value?.map(formatLegalBasis).join('\n') ?? '',
      isLongField: true,
    },
    {
      prop: 'plans',
      label: t('plan'),
      converter: (value: Project['plans']) =>
        (value ?? [])
          .map((v) => `${v.contents ?? ''}\n${v.url ?? ''}`)
          .join('\n'),
      isLongField: true,
    },
    {
      prop: 'sortOrder',
      label: t('project-order-number'),
      isLongField: true,
    },
    {
      prop: 'remark',
      label: t('remark'),
      isLongField: true,
    },
  ];

  return (
    <Card>
      <Row gutter={[8, 0]}>
        <DiffViewerTitleCols label={t('project')} />
        {diffViewConfig.map(({ prop, label, converter, isLongField }) => (
          <DiffViewerContentCols<Project>
            key={`project-${prop}`}
            legend={label}
            prop={prop}
            textConverter={converter}
            isLongField={isLongField}
            source={sourceProject}
            target={targetProject}
          />
        ))}
      </Row>
    </Card>
  );
};
